<template>
  <div class="main-contents">
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col-md-7">
          <h1 class="page-title">Connect to Meta</h1>
        </div>
      </div>
    </div>

    <div class="main-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-8 offset-md-2">
            <div class="card">
              <div class="card-body">
                <!-- Status Section -->
                <div class="status-section mb-4">
                  <div class="d-flex align-items-center gap-3 mb-3">
                    <h3 class="mb-0">Connection Status</h3>
                    <span
                      class="badge"
                      :class="connectionStatus === 'connected' ? 'bg-success' : 'bg-warning'"
                    >
                      {{ connectionStatus === 'connected' ? 'Connected' : 'Pending' }}
                    </span>
                  </div>
                </div>

                <!-- Description -->
                <div class="description-section mb-5">
                  <p class="text-muted">
                    By connecting your Meta account with Dealersgear Inc, you will be able to use your WhatsApp
                    Business Account for sending and receiving messages directly from this platform. This integration
                    allows you to manage your business communications seamlessly.
                  </p>
                </div>

                <!-- Connected Account Info (shown when connected) -->
                <div v-if="connectionStatus === 'connected'" class="connected-info-section mb-5 p-4 bg-light rounded">
                  <h5 class="mb-3">Connected Account Information</h5>
                  <div class="row">
                    <div class="col-md-6">
                      <p>
                        <strong>Business Name:</strong>
                        <br />
                        {{ connectedBusiness?.name || 'Not available' }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p>
                        <strong>WABA ID:</strong>
                        <br />
                        {{ connectedBusiness?.waba_id || 'Not available' }}
                      </p>
                    </div>
                  </div>
                  <div class="row mt-3">
                    <div class="col-md-6">
                      <p>
                        <strong>WhatsApp Number:</strong>
                        <br />
                        {{ connectedBusiness?.whatsapp_number || 'Not available' }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p>
                        <strong>Connected Date:</strong>
                        <br />
                        {{ connectedBusiness?.connected_date || 'Not available' }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Checkbox Agreement -->
                <div class="agreement-section mb-4">
                  <div class="form-check">
                    <input
                      v-model="agreeToTerms"
                      class="form-check-input"
                      type="checkbox"
                      id="agreeCheckbox"
                    />
                    <label class="form-check-label" for="agreeCheckbox">
                      I understand and agree that my business information will be shared with Meta for verification
                      and connection purposes.
                    </label>
                  </div>
                </div>

                <!-- Connect Button -->
                <div v-if="connectionStatus === 'pending'" class="button-section">
                  <button
                    @click="handleConnectClick"
                    :disabled="!agreeToTerms || isConnecting"
                    class="btn btn-primary"
                  >
                    <i class="fa fa-link mr-2"></i>
                    {{ isConnecting ? 'Connecting...' : 'Connect with Meta' }}
                  </button>
                  <small class="d-block text-muted mt-2">
                    You will be redirected to Meta to authorize the connection.
                  </small>
                </div>

                <!-- Disconnect Button (shown when connected) -->
                <div v-if="connectionStatus === 'connected'" class="button-section">
                  <button
                    @click="handleDisconnect"
                    class="btn btn-danger"
                    :disabled="isDisconnecting"
                  >
                    <i class="fa fa-times mr-2"></i>
                    {{ isDisconnecting ? 'Disconnecting...' : 'Disconnect' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global FB */

export default {
  name: 'MetaConnect',
  data() {
    return {
      metaAppId: '2198716410375102',
      configurationId: '1162464869302282',
      redirectUri: 'https://api.getstreamly.com/api/whatsapp/meta/callback',
      agreeToTerms: false,
      connectionStatus: 'pending', // 'pending' or 'connected'
      connectedBusiness: null,
      isDisconnecting: false,
      isConnecting: false,
      isLoading: true,
      fbSdkLoaded: false
    };
  },
  mounted() {
    this.loadFacebookSDK();
    this.checkConnectionStatus();
    window.showHideMainLoader(false);
  },
  methods: {
    loadFacebookSDK() {
      // Load Facebook SDK dynamically
      if (window.FB) {
        this.initializeFB();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.initializeFB();
      };
      document.body.appendChild(script);
    },

    initializeFB() {
      if (!window.FB) {
        console.error('Facebook SDK failed to load');
        return;
      }

      FB.init({
        appId: this.metaAppId,
        autoLogAppEvents: true,
        xfbml: false,
        version: 'v20.0',
      });

      this.fbSdkLoaded = true;
    },

    checkConnectionStatus() {
      // Check if user has connected Meta account
      // This would call your backend API to check the connection status
      // For now, we'll check localStorage or make an API call
      
      try {
        const storedConnection = localStorage.getItem('metaConnection');
        if (storedConnection) {
          const connection = JSON.parse(storedConnection);
          this.connectionStatus = connection.status || 'pending';
          if (connection.status === 'connected') {
            this.connectedBusiness = connection.business || null;
          }
        }
      } catch (error) {
        console.log('No previous connection found');
      }

      // You should also call your backend API here to verify:
      // this.fetchConnectionStatus();
      this.isLoading = false;
    },
    
    fetchConnectionStatus() {
      // Call your backend API to check connection status
      // GET /api/meta/connection-status
      // This will fetch the actual connection status from the database
    },

    handleConnectClick() {
      if (!this.agreeToTerms) {
        this.$notify({
          type: 'warning',
          message: 'Please agree to the terms before connecting'
        });
        return;
      }

      if (!this.fbSdkLoaded) {
        this.$notify({
          type: 'error',
          message: 'Facebook SDK is still loading. Please try again.'
        });
        return;
      }

      this.isConnecting = true;
      this.startEmbeddedSignup();
    },

    startEmbeddedSignup() {
      // First, perform FB.login to get necessary permissions
      FB.login(
        (response) => {
          if (response.authResponse) {
            // User is logged in, now show embedded signup or redirect
            this.handleEmbeddedSignup();
          } else {
            console.log('User cancelled login or did not fully authorize.');
            this.isConnecting = false;
            this.$notify({
              type: 'warning',
              message: 'Login was cancelled'
            });
          }
        },
        {
          scope: 'whatsapp_business_management,business_management',
          login_behavior: 'popup',
        }
      );
    },

    handleEmbeddedSignup() {
      // Check if FB.EmbeddedSignup is available
      if (window.FB && window.FB.EmbeddedSignup) {
        this.showEmbeddedSignup();
      } else {
        // Fallback to redirect-based flow
        this.redirectToEmbeddedSignup();
      }
    },

    showEmbeddedSignup() {
      // Trigger embedded signup via SDK
      try {
        FB.EmbeddedSignup.show({
          configuration_id: this.configurationId,
          redirect_uri: this.redirectUri,
          version: 'v2',
        });
      } catch (error) {
        console.error('Failed to show embedded signup:', error);
        this.redirectToEmbeddedSignup();
      }
    },

    redirectToEmbeddedSignup() {
      // Fallback: Construct OAuth URL for embedded signup
      const params = new URLSearchParams({
        client_id: this.metaAppId,
        display: 'popup',
        extras: JSON.stringify({
          setup: {
            whatsapp_business_account: {
              currency: 'USD',
            },
          },
        }),
        redirect_uri: this.redirectUri,
        response_type: 'code',
        scope: 'whatsapp_business_management,business_management',
        state: 'embedded_signup',
      });

      const authUrl = `https://www.facebook.com/v20.0/dialog/oauth?${params.toString()}`;
      window.location.href = authUrl;
    },

    async handleDisconnect() {
      // Use SweetAlert confirmation instead of native confirm (native was overwritten)
      const result = await window.swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to disconnect your Meta account?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, disconnect',
        cancelButtonText: 'Cancel'
      });

      if (!result || !result.isConfirmed) {
        return;
      }

      this.isDisconnecting = true;

      try {
        localStorage.removeItem('metaConnection');
      } catch (e) {
        console.warn('Failed to clear metaConnection from localStorage', e);
      }

      this.connectionStatus = 'pending';
      this.connectedBusiness = null;
      this.isDisconnecting = false;

      this.$router.push('/meta-connect');
    }
  }
};
</script>

<style scoped>
.main-body {
  padding: 30px 0;
}

.status-section {
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.description-section {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.connected-info-section {
  border-left: 4px solid #28a745;
}

.agreement-section {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.button-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.badge {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
}
</style>
